import { useCallback, useEffect, useState, useTransition } from 'react';
import { BrandCard } from './buds-header';
import { callApi, ShopifyAppApiError } from '@/lib/api';

type SyncScope = 'ALL' | 'SELECTED';

interface ProductCounts {
  all: number;
  selected: number;
}

interface Props {
  initialScope: SyncScope;
  onChange: (next: SyncScope) => void;
  onReconnectRequired: (message?: string) => void;
}

const OPTIONS: Array<{ value: SyncScope; label: string; hint: string }> = [
  {
    value: 'ALL',
    label: 'All products',
    hint: 'Every active product becomes a Buds reward. Set buds.sync_enabled to false to exclude one, or buds.force_sync to true to include a Draft or Unlisted one.',
  },
  {
    value: 'SELECTED',
    label: 'Selected products only',
    hint: 'Only active products with buds.sync_enabled set to true are synced, plus any product with buds.force_sync set to true.',
  },
];

export function SyncScopeCard({
  initialScope,
  onChange,
  onReconnectRequired,
}: Props) {
  const [scope, setScope] = useState<SyncScope>(initialScope);
  const [counts, setCounts] = useState<ProductCounts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  const loadCounts = useCallback(async () => {
    try {
      const body = await callApi<ProductCounts>(
        '/v1/shopify-app/sync/product-counts',
      );
      setCounts(body);
    } catch {
      // count is informational — the card works without it
    }
  }, []);

  useEffect(() => {
    void loadCounts();
  }, [loadCounts]);

  async function select(next: SyncScope) {
    if (next === scope) return;
    setError(null);
    const prev = scope;
    setScope(next);
    try {
      const body = await callApi<{ syncScope: SyncScope }>(
        '/v1/shopify-app/settings',
        { method: 'PATCH', body: { syncScope: next } },
      );
      setScope(body.syncScope);
      onChange(body.syncScope);
      void loadCounts();
    } catch (err) {
      setScope(prev);
      const e = err as ShopifyAppApiError;
      if (e.code === 'NOT_CONNECTED') {
        onReconnectRequired(e.message);
        return;
      }
      setError(`Could not update sync scope: ${e.message}`);
    }
  }

  const count =
    counts === null ? null : scope === 'ALL' ? counts.all : counts.selected;

  return (
    <BrandCard title="Which products sync to Buds">
      <div className="space-y-2">
        {OPTIONS.map((option) => {
          const selected = scope === option.value;
          return (
            <label
              key={option.value}
              className={`flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 transition ${
                selected
                  ? 'border-buds-green bg-buds-green/5'
                  : 'border-buds-dark/15 hover:border-buds-dark/30'
              } ${pending ? 'pointer-events-none opacity-60' : ''}`}
            >
              <input
                type="radio"
                name="sync-scope"
                value={option.value}
                checked={selected}
                onChange={() => startTransition(() => select(option.value))}
                disabled={pending}
                className="mt-0.5 h-4 w-4"
              />
              <span>
                <span className="block text-sm font-medium text-buds-dark">
                  {option.label}
                </span>
                <span className="mt-0.5 block text-xs text-buds-dark/60">
                  {option.hint}
                </span>
              </span>
            </label>
          );
        })}
      </div>
      {count != null && (
        <p className="mt-3 text-sm text-buds-dark/80">
          <span className="font-semibold text-buds-dark">{count}</span> product
          {count === 1 ? '' : 's'} will be synced on the next run.
        </p>
      )}
      {error && (
        <div className="mt-3 rounded-md border border-red-400/60 bg-red-50 px-3 py-2 text-xs text-red-900">
          {error}
        </div>
      )}
    </BrandCard>
  );
}
